import { useCallback, useEffect, useRef, useState } from 'react';

type ScrollState = {
  scrollable: boolean;
  atTop: boolean;
  atBottom: boolean;
};

export const useScrollState = () => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [state, setState] = useState<ScrollState>({
    scrollable: false,
    atTop: true,
    atBottom: true,
  });

  const compute = useCallback(() => {
    const el = ref.current;
    if (!el) {
      setState({ scrollable: false, atTop: true, atBottom: true });
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = el;
    const scrollable = scrollHeight > clientHeight + 1;
    const atTop = scrollTop <= 0;
    const atBottom = scrollTop + clientHeight >= scrollHeight - 1;

    setState({ scrollable, atTop, atBottom });
  }, []);

  useEffect(() => {
    compute();
  }, [compute]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onScroll = () => compute();
    el.addEventListener('scroll', onScroll, { passive: true });

    const ro = new ResizeObserver(() => compute());
    ro.observe(el);

    const mo = new MutationObserver(() => compute());
    mo.observe(el, { childList: true, subtree: true });

    return () => {
      el.removeEventListener('scroll', onScroll);
      ro.disconnect();
      mo.disconnect();
    };
  }, [compute]);

  const scrollToTop = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const scrollToBottom = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, []);

  return { ref, state, scrollToTop, scrollToBottom, recompute: compute };
};
